/**
 * The account page's contacts panel, and the dialog that edits them.
 *
 * Contact details used to be write-once — settable only in the "Add an
 * Account" form — so a rep who learned the buyer's email on their
 * third visit had nowhere to put it. This is where that gets fixed.
 *
 * Numbers and addresses are live links. Reps read this page standing
 * in front of the account on a phone; tapping to dial is the whole
 * reason for storing them.
 */

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';

import { saveAccountContacts, type AccountContact } from '../../api/field';
import { ContactsEditor } from './ContactsEditor';
import {
  draftsFromContacts,
  draftsToPayload,
  hasInvalidEmail,
  type ContactDraft,
} from './contactDrafts';
import { FONT_INTER, GOLD, HAIRLINE, INK, IVORY } from './formStyles';

/** Strip everything a dialer can't use. Keeps a leading +. */
const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

interface Props {
  accountId: number;
  contacts: AccountContact[];
}

export function AccountContactsPanel({ accountId, contacts }: Props) {
  const [editing, setEditing] = useState(false);

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: GOLD,
          }}
        >
          Contacts
        </Typography>

        <Button
          onClick={() => setEditing(true)}
          startIcon={
            contacts.length === 0 ? (
              <PersonAddAltIcon sx={{ fontSize: 16 }} />
            ) : (
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            )
          }
          size="small"
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12.5,
            color: GOLD,
            borderRadius: 1.5,
            '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.08)' },
          }}
        >
          {contacts.length === 0 ? 'Add Contact' : 'Edit'}
        </Button>
      </Stack>

      {contacts.length === 0 ? (
        <Box
          sx={{
            p: 2.5,
            textAlign: 'center',
            border: `1px dashed rgba(187, 140, 63, 0.32)`,
            borderRadius: 1.5,
            bgcolor: 'rgba(187, 140, 63, 0.04)',
            fontFamily: FONT_INTER,
            color: 'rgba(15, 10, 7, 0.6)',
            fontSize: 13.5,
          }}
        >
          Nobody on file yet — add the name and number of whoever you deal with here.
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {contacts.map((contact, index) => (
            <ContactCard key={contact.id} contact={contact} isPrimary={index === 0} />
          ))}
        </Box>
      )}

      <ContactsDialog
        open={editing}
        onClose={() => setEditing(false)}
        accountId={accountId}
        contacts={contacts}
      />
    </Box>
  );
}

function ContactCard({ contact, isPrimary }: { contact: AccountContact; isPrimary: boolean }) {
  return (
    <Box
      sx={{
        p: { xs: 1.75, md: 2 },
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 1.5,
        bgcolor: 'rgba(253, 251, 245, 0.85)',
        // The first contact is the one to try first; a hairline of
        // gold says so without spending a badge on it.
        borderLeft: isPrimary ? `3px solid ${GOLD}` : `1px solid ${HAIRLINE}`,
      }}
    >
      <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
        <Typography
          sx={{ fontFamily: FONT_INTER, fontSize: 14, fontWeight: 700, color: INK, minWidth: 0 }}
        >
          {contact.name || 'No name on file'}
        </Typography>
        {contact.role && (
          <Typography
            sx={{ fontFamily: FONT_INTER, fontSize: 11.5, color: 'rgba(15, 10, 7, 0.5)' }}
          >
            {contact.role}
          </Typography>
        )}
      </Stack>

      <Stack gap={0.35} sx={{ mt: 0.75 }}>
        {contact.phones.map((phone) => (
          <ContactLink key={phone} href={telHref(phone)} icon={<PhoneIphoneIcon />} label={phone} />
        ))}
        {contact.emails.map((email) => (
          <ContactLink
            key={email}
            href={`mailto:${email}`}
            icon={<MailOutlineIcon />}
            label={email}
          />
        ))}
        {contact.phones.length === 0 && contact.emails.length === 0 && (
          <Typography
            sx={{ fontFamily: FONT_INTER, fontSize: 12.5, color: 'rgba(15, 10, 7, 0.45)' }}
          >
            No number or email yet
          </Typography>
        )}
      </Stack>

      {contact.notes && (
        <Typography
          sx={{
            mt: 0.75,
            fontFamily: FONT_INTER,
            fontSize: 12,
            color: 'rgba(15, 10, 7, 0.55)',
            lineHeight: 1.5,
          }}
        >
          {contact.notes}
        </Typography>
      )}
    </Box>
  );
}

function ContactLink({ href, icon, label }: { href: string; icon: ReactElement; label: string }) {
  return (
    <Stack
      component="a"
      href={href}
      direction="row"
      alignItems="center"
      gap={0.75}
      sx={{
        color: 'rgba(15, 10, 7, 0.7)',
        textDecoration: 'none',
        minWidth: 0,
        '&:hover': { color: GOLD },
        '& svg': { fontSize: 14, color: 'rgba(15, 10, 7, 0.35)', flexShrink: 0 },
        '&:hover svg': { color: GOLD },
      }}
    >
      {icon}
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 13,
          color: 'inherit',
          wordBreak: 'break-word',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  accountId: number;
  contacts: AccountContact[];
}

function ContactsDialog({ open, onClose, accountId, contacts }: DialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const [drafts, setDrafts] = useState<ContactDraft[]>(() => draftsFromContacts(contacts));
  const [error, setError] = useState<string | null>(null);

  // Re-seed each time it opens, so an abandoned edit doesn't survive
  // into the next one and so a save made elsewhere is picked up.
  useEffect(() => {
    if (!open) return;
    setDrafts(draftsFromContacts(contacts));
    setError(null);
  }, [open, contacts]);

  const save = useMutation({
    mutationFn: () => saveAccountContacts(accountId, draftsToPayload(drafts)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field', 'account', accountId] });
      onClose();
    },
    onError: () => setError('Couldn’t save those contacts. Try again.'),
  });

  const emailInvalid = hasInvalidEmail(drafts);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          border: fullScreen ? 'none' : `1px solid ${HAIRLINE}`,
          boxShadow: fullScreen ? 'none' : '0 24px 60px -20px rgba(15, 10, 7, 0.35)',
          m: fullScreen ? 0 : 2,
          maxHeight: fullScreen ? '100%' : 'calc(100% - 32px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: IVORY,
          borderBottom: `1px solid ${HAIRLINE}`,
          py: { xs: 1.75, sm: 2 },
          px: { xs: 2.5, sm: 3 },
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: GOLD,
            mb: 0.25,
          }}
        >
          Account
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontWeight: 700,
            fontSize: { xs: 17, sm: 18 },
            letterSpacing: '-0.01em',
            color: INK,
          }}
        >
          Who we deal with here
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '22px !important', px: { xs: 2.5, sm: 3 }, pb: 1 }}>
        <Stack gap={2}>
          {error && (
            <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
              {error}
            </Alert>
          )}
          <ContactsEditor value={drafts} onChange={setDrafts} />
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 11.5,
              color: 'rgba(15, 10, 7, 0.5)',
              lineHeight: 1.5,
            }}
          >
            Removing a contact here removes them from the account. The first one listed is who the
            team tries first.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          gap: 1,
          borderTop: `1px solid ${HAIRLINE}`,
          bgcolor: 'rgba(253, 249, 240, 0.75)',
          backdropFilter: 'blur(6px)',
          position: 'sticky',
          bottom: 0,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            borderRadius: 2,
            px: 2,
            color: 'rgba(15, 10, 7, 0.6)',
            '&:hover': { bgcolor: 'rgba(15, 10, 7, 0.05)' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => save.mutate()}
          disabled={emailInvalid || save.isPending}
          sx={{
            fontFamily: FONT_INTER,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 14,
            borderRadius: 2,
            px: 2.5,
            py: 0.9,
            bgcolor: GOLD,
            boxShadow: '0 6px 16px -6px rgba(187, 140, 63, 0.8)',
            '&:hover': { bgcolor: '#a67c34' },
            '&.Mui-disabled': {
              bgcolor: 'rgba(187, 140, 63, 0.28)',
              color: '#fff',
              boxShadow: 'none',
            },
          }}
        >
          {save.isPending ? 'Saving…' : 'Save Contacts'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
